import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useAdminSubscriptions } from '@dashboard/hooks/queries/useAdminQueries';

import { formatPrice } from '@dashboard/services/formatting';
import getSubscriptionIntervalWithSlash from '@dashboard/services/formatting/getSubscriptionIntervalWithSlash';
import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Checkbox, FormElement, Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import ViewAsMerchantButton from '@dashboard/admin/components/ViewAsMerchantButton';

import AdminZeroAmountFilter, {
  getZeroAmountFilter,
  parseZeroAmount,
} from '@dashboard/admin/components/AdminZeroAmountFilter';

const Subscriptions = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState('livemode' in query ? query.livemode : true);
  const [status, setStatus] = useState(query.status || 'all');
  const [abandonmentEmailSent, setAbandonmentEmailSent] = useState(
    query.abandonmentEmailSent === true,
  );
  const [zeroAmount, setZeroAmount] = useState(parseZeroAmount(query.zeroAmount));

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
        ...(abandonmentEmailSent ? { abandonmentEmailSent } : {}),
        ...(zeroAmount !== 'include' ? { zeroAmount } : {}),
      }),
    });
  }, [sortBy, sortDirection, livemode, status, abandonmentEmailSent, zeroAmount]);

  // Build filters object for the hook
  const filters = {
    sortBy,
    sortDirection,
    ...(livemode ? { livemode } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(abandonmentEmailSent ? { abandonmentEmailSent } : {}),
    // Matches the recurring amount shown in the Amount column, which is stored before any coupon.
    ...getZeroAmountFilter(zeroAmount),
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useAdminSubscriptions(filters);

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'page.title',
        id: 'page.title',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Checkout" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2">
            <Link href={`/pages/${row.original.page._id}/customize`}>
              {row.original.product.title}
            </Link>
            {!row.original.livemode && (
              <Tooltip label={row.original.paymentError} side="right">
                <StatusBadge status="test" withInfoIcon={!!row.original.paymentError}>
                  Test
                </StatusBadge>
              </Tooltip>
            )}
          </div>
        ),
        enableSorting: true,
      },
      {
        id: 'type',
        header: 'Type',
        cell: () => 'Checkout',
        enableSorting: false,
      },
      {
        accessorKey: 'amount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Amount" />,
        cell: ({ row }) => (
          <>
            {formatPrice({
              amount: row.original.amount,
              currency: row.original.currency,
            })}{' '}
            {getSubscriptionIntervalWithSlash(row.original.interval, row.original.intervalCount)}
          </>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        id: 'seller.displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => (
          <div className="relative flex items-center">
            <Link href={`/admin/merchants/${row.original.seller?._id}`}>
              {row.original.seller?.displayName || row.original.seller?.name}
            </Link>
            {row.original.seller?.disabled && (
              <Badge variant="secondary" className="ml-2">
                Disabled
              </Badge>
            )}
          </div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm'),
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2">
            <Tooltip label={row.original.paymentError} side="right">
              <StatusBadge
                status={row.original.status}
                withInfoIcon={!!row.original.paymentError}
              />
            </Tooltip>
          </div>
        ),
        enableSorting: true,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <ViewAsMerchantButton
            sellerId={row.original.seller?._id}
            path={`/subscriptions/${row.original._id}`}
          >
            View subscription
          </ViewAsMerchantButton>
        ),
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    [],
  );

  // Filter controls as custom actions
  const filterControls = (
    <div className="flex items-end gap-x-2">
      <FormElement className="!mb-0">
        <div className="flex items-center gap-x-2 w-full">
          <Checkbox
            id="livemode"
            value={!livemode}
            checked={!livemode}
            onChange={() => setLivemode(livemode ? false : true)}
          />

          <Label isInline htmlFor="livemode">
            Include test payments
          </Label>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <div className="flex items-center gap-x-2 w-full">
          <Checkbox
            id="abandonmentEmailSent"
            checked={abandonmentEmailSent}
            onChange={() => setAbandonmentEmailSent(!abandonmentEmailSent)}
          />

          <Label isInline htmlFor="abandonmentEmailSent">
            Abandonment email sent
          </Label>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="status">Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger id="status" className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="recovered">Recovered</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="past_due">Overdue</SelectItem>
            <SelectItem value="incomplete">Incomplete</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="abandoned">Abandoned</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <AdminZeroAmountFilter value={zeroAmount} onChange={setZeroAmount} />
    </div>
  );

  return (
    <div className="pr-4">
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        toolbar={{
          customActions: filterControls,
        }}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No subscriptions found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default Subscriptions;
